/**
 * Mensaje de bienvenida de cada agente: lo primero que ve el usuario al abrir su chat.
 *
 * Se pinta en el navegador y NO se envía al modelo, así que no consume tokens. Para cambiar un
 * texto basta con editar este archivo; la clave es el id del agente (el mismo de agentIcons).
 *
 * Criterio: cada capacidad y cada ejemplo de aquí se comprobó preguntándole al agente real
 * (19 de septiembre de 2026). Si cambian las instrucciones o herramientas de un agente en
 * Foundry, hay que volver a comprobarlo: un saludo que promete algo que el agente ya no puede
 * cumplir es peor que no tener saludo.
 */
export interface AgentWelcome {
  /** Primera frase: quién es y con qué información trabaja. */
  saludo: string;
  /** Qué puede hacer, en frases cortas. */
  puedo: string[];
  /** Preguntas de ejemplo. Al pulsarlas se escriben en el cuadro, no se envían solas. */
  ejemplos: string[];
  /** Aclaración que evita malentendidos: alcance, límites o permisos. */
  nota?: string;
}

export const AGENT_WELCOME: Record<string, AgentWelcome> = {
  DELTA: {
    saludo: 'Hola, soy Delta. Consulto en tiempo real la información de tu empresa en Dynamics 365 Business Central.',
    puedo: [
      'Ver las empresas disponibles en tu Business Central',
      'Consultar clientes, productos y proveedores, con sus saldos',
      'Revisar facturas y órdenes de venta y de compra'
    ],
    ejemplos: [
      '¿Qué empresas tengo disponibles en Business Central?',
      'Muéstrame los clientes con saldo pendiente',
      'Muéstrame las últimas facturas de venta'
    ],
    nota: 'Si no me dices la empresa, te preguntaré cuál. Muestro hasta 5 registros por consulta; pídeme más si los necesitas.'
  },

  ALPHERATZ: {
    saludo: 'Hola, soy Alpheratz. Te doy un panorama ejecutivo de tus sistemas Aspel: SAE, COI y NOI.',
    puedo: [
      'Resumir cartera de clientes y proveedores, ventas, compras e inventario (SAE)',
      'Resumir pólizas, saldos y catálogo de cuentas (COI)',
      'Resumir departamentos, puestos y nómina (NOI)'
    ],
    ejemplos: [
      '¿Cómo está mi cartera de clientes?',
      '¿Cómo está mi inventario?',
      'Dame el resumen de ventas y facturación',
      '¿Cuántos puestos y departamentos tengo registrados en nómina?'
    ],
    nota: 'Mis consultas son resúmenes globales: no filtro por fechas, clientes, productos ni empleados específicos.'
  },

  // MU: volver a comprobarlo cuando se le cargue la nueva información y el prompt definitivo
  // en Foundry. Estos ejemplos se verificaron con el MU que sí tiene base de conocimiento; el
  // del proyecto M31-Suite hoy responde de forma vaga.
  MU: {
    saludo: 'Hola, soy MU, el asistente de IONET. Te cuento sobre nuestros servicios y te ayudo a solicitar una cotización.',
    puedo: [
      'Conocer los servicios de IONET: nube, inteligencia artificial, Microsoft 365, Dynamics y desarrollo',
      'Entender qué es M31 AI Suite y para qué sirve cada uno de sus agentes',
      'Solicitar una cotización o que un especialista te contacte'
    ],
    ejemplos: [
      '¿Qué servicios ofrece IONET?',
      '¿Qué es M31 AI Suite?',
      'Quiero cotizar un proyecto'
    ]
  },

  MIRACH: {
    saludo: 'Hola, soy Mirach. Busco en los documentos de SharePoint de tu organización y te respondo citando la fuente.',
    puedo: [
      'Encontrar políticas, manuales y procedimientos',
      'Resumir lo que dice un documento',
      'Indicarte de qué documento sale cada respuesta'
    ],
    ejemplos: [
      '¿Qué políticas internas hay publicadas?',
      '¿Qué documentos de recursos humanos hay disponibles?',
      'Busca documentos sobre procedimientos de la empresa'
    ],
    nota: 'Uso tus propios permisos: solo leo los documentos a los que tú ya tienes acceso.'
  }

  // CUSTOM_AGENT no tiene texto propio a propósito: se está rediseñando en Foundry, sin
  // búsqueda web. Mientras tanto usa DEFAULT_AGENT_WELCOME, que es cierto en ambas versiones.
  // Cuando esté listo, comprobar sus respuestas reales y escribir aquí su bienvenida.
};

/** Saludo para un agente que aún no tenga texto propio en este archivo. */
export const DEFAULT_AGENT_WELCOME: AgentWelcome = {
  saludo: 'Hola, estoy listo para ayudarte.',
  puedo: [],
  ejemplos: ['¿En qué me puedes ayudar?']
};
